<template>
  <div class="login-container">
    <div class="login-card">
      <!-- 로고/타이틀 -->
      <div class="login-header">
        <h1 class="login-title">CAION</h1>
        <p class="login-subtitle">통합 관리 시스템</p>
        <!-- 테스트 서버 안내 -->
        <div v-if="isTestServer" class="test-server-notice">
          <v-icon size="18" class="mr-1">mdi-alert-circle</v-icon>
          <span>테스트 서버: 관리자 계정만 로그인 가능</span>
        </div>
      </div>

      <!-- 로그인 폼 -->
      <v-form ref="formRef" @submit.prevent="handleLogin()" class="login-form">
        <div class="input-group">
          <v-text-field
            v-model="loginId"
            label="아이디"
            prepend-inner-icon="mdi-account"
            variant="outlined"
            density="comfortable"
            :rules="[rules.required]"
            :disabled="loading"
            @keyup.enter="handleLogin"
          />
        </div>

        <div class="input-group">
          <v-text-field
            v-model="loginPw"
            label="비밀번호"
            prepend-inner-icon="mdi-lock"
            :type="showPassword ? 'text' : 'password'"
            :append-inner-icon="showPassword ? 'mdi-eye' : 'mdi-eye-off'"
            variant="outlined"
            density="comfortable"
            :rules="[rules.required]"
            :disabled="loading"
            @click:append-inner="showPassword = !showPassword"
            @keyup.enter="handleLogin"
          />
        </div>

        <!-- 체크박스 -->
        <div class="checkbox-group">
          <v-checkbox
            v-model="saveId"
            label="아이디 저장"
            density="compact"
            hide-details
            color="primary"
          />
          <v-checkbox
            v-model="autoLogin"
            label="자동 로그인"
            density="compact"
            hide-details
            color="primary"
          />
        </div>
        <p class="auto-login-hint">자동 로그인을 켜면 브라우저를 닫아도 최대 30일간 로그인 상태가 유지됩니다. (보안상 하루에 한 번 정도 조용히 갱신됩니다)</p>

        <!-- 로그인 버튼 -->
        <v-btn
          type="submit"
          size="large"
          block
          :loading="loading"
          :disabled="loading"
          variant="flat"
          class="login-btn btn-search"
        >
          <v-icon left>mdi-login</v-icon>
          로그인
        </v-btn>
      </v-form>

      <!-- 안내 문구 -->
      <div class="login-footer">
      </div>
    </div>

    <!-- 배경 장식 -->
    <div class="bg-decoration">
      <div class="circle circle-1"></div>
      <div class="circle circle-2"></div>
      <div class="circle circle-3"></div>
    </div>
    
    <!-- 사업구분 선택 다이얼로그 -->
    <v-dialog v-model="bizGubunDialog" max-width="420" persistent>
      <v-card class="biz-gubun-dialog">
        <v-card-title class="dialog-header">
          <v-icon class="mr-2">mdi-office-building-marker</v-icon>
          사업구분 선택
          <button
            type="button"
            class="biz-gubun-dialog__close"
            :disabled="loading"
            aria-label="닫기"
            @click="cancelBizGubunSelect"
          >
            <v-icon size="18">mdi-close</v-icon>
          </button>
        </v-card-title>
        <v-card-text class="dialog-body">
          <p class="dialog-desc">동일한 아이디로 등록된 여러 기관이 있습니다.<br>접속할 기관을 선택해주세요.</p>
          <div class="biz-gubun-list">
            <div 
              v-for="member in bizGubunMembers" 
              :key="member.idx"
              class="biz-gubun-item"
              :class="{ selected: selectedMemberIdx === member.idx }"
              @click="selectedMemberIdx = member.idx"
            >
              <div class="biz-gubun-color" :style="{ backgroundColor: member.bizGubunColor }"></div>
              <div class="biz-gubun-info">
                <span class="member-name">{{ member.name }}</span>
                <span class="member-biz">{{ member.bizGubunName || member.bizGubun }}</span>
              </div>
              <v-icon v-if="selectedMemberIdx === member.idx" color="primary" class="check-icon">mdi-check-circle</v-icon>
            </div>
          </div>
        </v-card-text>
        <v-card-actions class="dialog-actions">
          <v-btn variant="text" class="btn-cancel" @click="cancelBizGubunSelect">취소</v-btn>
          <v-spacer />
          <v-btn 
            variant="flat"
            class="btn-confirm"
            :disabled="!selectedMemberIdx"
            :loading="loading"
            @click="confirmBizGubunSelect"
          >
            선택 완료
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';

const store = useStore();
const router = useRouter();

const domainPrefix = window.location.hostname.includes('test.caion') ? 'test_' : '';
const AUTO_LOGIN_PREF_KEY = `${domainPrefix}caion_auto_login_pref`;

// 테스트 서버 여부
const isTestServer = computed(() => window.location.hostname.includes('test.caion'));

// 폼 ref
const formRef = ref<any>(null);

// 입력값
const loginId = ref('');
const loginPw = ref('');
const showPassword = ref(false);
const saveId = ref(false);
const autoLogin = ref(false);

// 사업구분 선택 다이얼로그
const bizGubunDialog = ref(false);
const bizGubunMembers = ref<any[]>([]);
const selectedMemberIdx = ref<number | null>(null);

// 로딩 상태
const loading = computed(() => store.getters.isLoading);

// 유효성 검사 규칙
const rules = {
  required: (v: string) => !!v || '필수 입력 항목입니다.'
};

watch(autoLogin, (on) => {
  localStorage.setItem(AUTO_LOGIN_PREF_KEY, on ? '1' : '0');
  if (on) saveId.value = true;
});

watch(saveId, (on) => {
  if (!on && autoLogin.value) autoLogin.value = false;
});

const goHome = (userType: string) => {
  if (userType === 'member') {
    router.push('/m/info');
  } else {
    router.push('/member');
  }
};

// 로그인 처리
const handleLogin = async (memberCode?: number) => {
  const { valid } = await formRef.value.validate();
  
  if (!valid) return;

  const result = await store.dispatch('login', {
    loginId: loginId.value,
    loginPw: loginPw.value,
    saveId: saveId.value,
    autoLogin: autoLogin.value,
    memberCode
  });

  if (result?.requireBizGubunSelect) {
    bizGubunMembers.value = result.members || [];
    selectedMemberIdx.value = null;
    bizGubunDialog.value = true;
    return;
  }

  if (result?.success && result.user) {
    goHome(result.user.userType);
  }
};

// 사업구분 선택 취소
const cancelBizGubunSelect = () => {
  bizGubunDialog.value = false;
  bizGubunMembers.value = [];
  selectedMemberIdx.value = null;
};

// 사업구분 선택 완료
const confirmBizGubunSelect = async () => {
  if (!selectedMemberIdx.value) return;
  
  bizGubunDialog.value = false;
  await handleLogin(selectedMemberIdx.value);
};

// 초기화
onMounted(() => {
  const savedLoginId = store.getters.savedId;
  if (savedLoginId) {
    loginId.value = savedLoginId;
    saveId.value = true;
  }

  if (localStorage.getItem(AUTO_LOGIN_PREF_KEY) === '1') {
    autoLogin.value = true;
    saveId.value = true;
  }
  
  if (store.getters.isLoggedIn) {
    goHome(store.getters.userType);
  }
});
</script>
